import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { ServerService } from '@affine/core/modules/cloud';
import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { EmbeddingSettings } from '@affine/core/modules/workspace-indexer-embedding';
import { ServerDeploymentType } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  AiEmbeddingIcon,
  CollaborationIcon,
  IntegrationsIcon,
  PaymentIcon,
  PropertyIcon,
  SaveIcon,
  SettingsIcon,
  FolderIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useMemo } from 'react';

import type { SettingSidebarItem, SettingState } from '../types';
import { WorkspaceSettingBilling } from './billing';
import { IntegrationSetting } from './integration';
import { WorkspaceSettingLicense } from './license';
import { MembersPanel } from './members';
import { WorkspaceSettingDetail } from './preference';
import { WorkspaceSettingMyUploads } from './my-uploads';
import { WorkspaceSettingProperties } from './properties';
import { WorkspaceSettingStorage } from './storage';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';

export const WorkspaceSetting = ({
  activeTab,
  onCloseSetting,
  onChangeSettingState,
}: {
  activeTab: SettingTab;
  onCloseSetting: () => void;
  onChangeSettingState: (settingState: SettingState) => void;
}) => {
  switch (activeTab) {
    case 'workspace:my-uploads':
      return <WorkspaceSettingMyUploads onCloseSetting={onCloseSetting} />;
    case 'workspace:preference':
      return <WorkspaceSettingDetail onCloseSetting={onCloseSetting} />;
    case 'workspace:properties':
      return <WorkspaceSettingProperties />;
    case 'workspace:members':
      return (
        <MembersPanel
          onCloseSetting={onCloseSetting}
          onChangeSettingState={onChangeSettingState}
        />
      );
    case 'workspace:billing':
      return <WorkspaceSettingBilling />;
    case 'workspace:storage':
      return <WorkspaceSettingStorage onCloseSetting={onCloseSetting} />;
    case 'workspace:license':
      return <WorkspaceSettingLicense onCloseSetting={onCloseSetting} />;
    case 'workspace:integrations':
      return <IntegrationSetting />;
    case 'workspace:embedding':
      return <EmbeddingSettings />;
    default:
      return null;
  }
};

export const useWorkspaceSettingList = (): SettingSidebarItem[] => {
  const workspaceService = useService(WorkspaceService);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const serverService = useService(ServerService);
  const permission = useService(WorkspacePermissionService).permission;
  const isExternalCollaborator = useLiveData(
    permission.isExternalCollaborator$
  );
  const isAdmin = useLiveData(permission.isAdmin$);
  const isOwner = useLiveData(permission.isOwner$);

  const isManager = isAdmin || isOwner;

  const isSelfhosted = useLiveData(
    serverService.server.config$.selector(
      c => c.type === ServerDeploymentType.Selfhosted
    )
  );

  const t = useI18n();

  const showBilling =
    !isSelfhosted && information?.isTeam && information?.isOwner;
  const showLicense = information?.isOwner && isSelfhosted;
  const items = useMemo<SettingSidebarItem[]>(() => {
    return [
      {
        key: 'workspace:my-uploads',
        title: '我的上传',
        icon: <FolderIcon />,
        testId: 'workspace-setting:my-uploads',
      },
      {
        key: 'workspace:preference',
        title: t['com.affine.settings.workspace.preferences'](),
        icon: <SettingsIcon />,
        testId: 'workspace-setting:preference',
      },
      !isExternalCollaborator && {
        key: 'workspace:properties',
        title: t['com.affine.settings.workspace.properties'](),
        icon: <PropertyIcon />,
        testId: 'workspace-setting:properties',
      },
      !isExternalCollaborator && {
        key: 'workspace:members',
        title: t['Members'](),
        icon: <CollaborationIcon />,
        testId: 'workspace-setting:members',
      },
      !isExternalCollaborator &&
        isManager && {
          key: 'workspace:integrations',
          title: t['com.affine.integration.integrations'](),
          icon: <IntegrationsIcon />,
          testId: 'workspace-setting:integrations',
        },
      !isExternalCollaborator &&
        isManager && {
          key: 'workspace:storage',
          title: t['Storage'](),
          icon: <SaveIcon />,
          testId: 'workspace-setting:storage',
        },
      !isExternalCollaborator &&
        isManager && {
          key: 'workspace:embedding',
          title:
            t[
              'com.affine.settings.workspace.indexer-embedding.embedding.title'
            ](),
          icon: <AiEmbeddingIcon />,
          testId: 'workspace-setting:embedding',
        },
      // showBilling && {
      //   key: 'workspace:billing' as SettingTab,
      //   title: t['com.affine.settings.workspace.billing'](),
      //   icon: <PaymentIcon />,
      //   testId: 'workspace-setting:billing',
      // },
    ].filter((item): item is SettingSidebarItem => !!item);
  }, [showBilling, showLicense, t, isExternalCollaborator]);

  return items;
};
